import { Controller } from "@hotwired/stimulus"

// THE client control (texting P3). The rows are server-rendered (every
// client the viewer can text, with data-active); this only decides which
// show. Empty query: active clients. Typed query: every client, an
// inactive one labelled "(Not Active)" by the markup. Enter picks the
// highlighted row or the ONLY visible row; a blind first-hit Enter on a
// control that assigns a client is a wrong-client vector, so it does
// nothing else. Dispatches client-picker:change {id, name, element}.
export default class extends Controller {
  static targets = ["value", "input", "list", "item", "empty", "clear"]
  static values = { selected: String }

  connect() {
    this.highlighted = -1
    this.label = ""
    if (this.selectedValue) {
      const li = this.itemTargets.find((el) => el.dataset.clientId === this.selectedValue)
      if (li) this.select(li, { silent: true })
    }
  }

  open() { this.filter() }

  filter() {
    const q = this.inputTarget.value.trim().toLowerCase()
    // Editing the text after a pick un-picks: the id must never outlive
    // the name the person can see.
    if (this.valueTarget.value && q !== this.label.toLowerCase()) this.unset()
    this.highlighted = -1
    let shown = 0
    this.itemTargets.forEach((el) => {
      const hit = q === "" ? el.dataset.active === "true" : el.dataset.search.includes(q)
      el.hidden = !hit
      el.classList.remove("is-active")
      el.setAttribute("aria-selected", "false")
      if (hit) shown++
    })
    this.emptyTarget.hidden = shown > 0
    this.listTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  keydown(event) {
    const visible = this.itemTargets.filter((el) => !el.hidden)
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      if (this.listTarget.hidden) this.filter()
      if (visible.length === 0) return
      const delta = event.key === "ArrowDown" ? 1 : -1
      this.highlighted = Math.min(Math.max(this.highlighted + delta, 0), visible.length - 1)
      visible.forEach((el, i) => {
        el.classList.toggle("is-active", i === this.highlighted)
        el.setAttribute("aria-selected", i === this.highlighted ? "true" : "false")
      })
      this.inputTarget.setAttribute("aria-activedescendant", visible[this.highlighted].id)
      visible[this.highlighted].scrollIntoView({ block: "nearest" })
    } else if (event.key === "Enter") {
      if (this.listTarget.hidden) return
      event.preventDefault()
      if (this.highlighted >= 0 && visible[this.highlighted]) this.select(visible[this.highlighted])
      else if (visible.length === 1 && this.inputTarget.value.trim() !== "") this.select(visible[0])
    } else if (event.key === "Escape") {
      event.stopPropagation()
      this.close()
    }
  }

  pick(event) {
    event.preventDefault() // keep focus so blur does not close the list first
    this.select(event.currentTarget)
  }

  select(li, { silent = false } = {}) {
    const inactive = li.dataset.active !== "true"
    this.label = inactive ? `${li.dataset.name} (Not Active)` : li.dataset.name
    this.valueTarget.value = li.dataset.clientId
    this.inputTarget.value = this.label
    this.clearTarget.hidden = false
    this.close()
    if (!silent) this.dispatch("change", { detail: { id: li.dataset.clientId, name: li.dataset.name, element: li } })
  }

  reset() {
    this.unset()
    this.inputTarget.value = ""
    this.inputTarget.focus()
  }

  unset() {
    this.valueTarget.value = ""
    this.label = ""
    this.clearTarget.hidden = true
    this.dispatch("change", { detail: { id: "", name: "", element: null } })
  }

  blur() { setTimeout(() => this.close(), 120) }

  close() {
    this.listTarget.hidden = true
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
  }
}
